





































































































































































































































































/* General */
import Vue from 'vue'
import omit from 'lodash/omit'

/* Mixins */

/* Components */

/* Helpers */
import ColorAssistant from '@/helpers/color-assistant'
import LocalStore from '@/helpers/localstore'

/* Data types */
import { EEntities } from '@/types'
import { ICommentOrReplyData } from '@/types/data-objects/items'
import { IAttachment } from '@/types/data-objects/attachments'
import {
  IAtMentionUser,
  IConvertedSavedReply,
  IItemResponder,
  IPersonBase,
  IPersonWithEmail,
  IPriorityLevelForSelection,
  IStatusForSelection,
} from '@/api/types/response.types'

/* API */
import FilesAPI from '@/api/modules/files.api'
import { IJobInfo } from '@/types/data-objects/items'

export default Vue.extend({
  name: 'CommentOrReply',

  props: {
    entityId: {
      type: Number,
      required: true,
    },

    itemId: {
      type: Number,
      required: true,
    },

    author: {
      type: Object as () => IPersonBase,
      default: null,
    },

    cc: {
      type: Array as () => string[],
      default: (): string[] => [],
    },

    customer: {
      type: Object as () => IPersonWithEmail,
      default: null,
    },

    /* Editor */
    mentionUsers: {
      type: Array as () => IAtMentionUser[],
      required: true,
    },

    /* Assignee picker */
    assigneeId: {
      type: Number,
      default: null,
    },

    assigneesOptions: {
      type: Array as () => IPersonBase[],
      default: (): IPersonBase[] => [],
    },

    /* Priority */
    priorityId: {
      type: Number,
      required: true,
    },

    priorityOptions: {
      type: Array as () => IPriorityLevelForSelection[],
      required: true,
    },

    /* Status */
    statusId: {
      type: Number,
      required: true,
    },

    statusOptions: {
      type: Array as () => IStatusForSelection[],
      required: true,
    },

    /* Due date */
    dueDate: {
      type: String,
      default: null,
    },

    /* Watchers picker */
    watcherIds: {
      type: Array as () => number[],
      required: true,
    },

    watchersOptions: {
      type: Array as () => IPersonWithEmail[],
      required: true,
    },

    replySignature: {
      type: String,
      default: '',
    },

    type: {
      type: String as () => EEntities,
      default: EEntities.MAILBOXES,
    },

    savedReplies: {
      type: Array as () => IConvertedSavedReply[],
      default: (): IConvertedSavedReply[] => [],
    },

    respondersSinceLastView: {
      type: Array as () => IItemResponder[],
      default: (): IItemResponder[] => [],
    },

    jobInfo: {
      type: Object as () => IJobInfo,
      default: null,
    },

    isTrafficCopChecking: {
      type: Boolean,
      default: false,
    },

    isSubmitting: {
      type: Boolean,
      default: false,
    },

    isPostSubmitPreRefetchData: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      ColorAssistant,
      mode: 'comment',
      isEditing: false,
      formData: {
        to: '',
        cc: [],
        message: '',
        assigneeID: null,
        dueDate: null,
        statusID: null,
        priorityID: null,
        listWatcherIDs: [],
        listAtMentionIDs: [],
        listFileUploadIDs: [],
      } as ICommentOrReplyData,
      internalAttachments: [],
      internalCustomer: {
        email: '',
        name: '',
        id: 0,
      },
      autoFocusEditor: false,
    }
  },

  computed: {
    isInternalCustomerEmpty(): boolean {
      return !this.internalCustomer || this.internalCustomer.email === ''
    },
    replyToLabel(): string {
      /* Case user has X'd out the customer and waiting to add a new one */
      /* Reply button will disable too. */
      if (this.isInternalCustomerEmpty) {
        return 'Reply'
      } else if (this.$screen.isTabletOrDesktop(this.$mq)) {
        return `Reply to ${this.internalCustomer.name}`
      } else {
        return `Reply to Customer`
      }
    },
    hasJobs(): boolean {
      return this.jobInfo !== null
    },
    hasRespondersSinceLastView(): boolean {
      return this.respondersSinceLastView.length > 0
    },
    trafficCopText(): string {
      if (!this.hasRespondersSinceLastView) {
        return ''
      }

      if (this.respondersSinceLastView.length > 1) {
        return `✋ <strong>Wait! Before we send this...</strong><br><strong>${
          this.respondersSinceLastView.length
        } responses</strong> were added since you started responding. <u>Click here to see what they wrote</u>.`
      }

      const lastResponder = this.respondersSinceLastView[0]

      if (lastResponder.isReply) {
        return `✋ <strong>Wait! Before we send this...</strong><br><strong>${
          lastResponder.name
        }</strong> replied since you started responding. <u>Click here to see what they wrote</u>.`
      } else {
        return `✋ <strong>Wait! Before we send this...</strong><br><strong>${
          lastResponder.name
        }</strong> commented since you started responding. <u>Click here to see what they wrote</u>.`
      }
    },
    possiblyMeantToComment(): boolean {
      return this.formData.message.includes('<span class="mention"')
    },

    possiblyMeantToCommentText(): string {
      if (!this.possiblyMeantToComment) {
        return ''
      }
      return `✋ You @-mentioned someone. <u>Click here to add a comment</u> or click below if you really mean to reply to the customer.`
    },
    selectedWatcherIds: {
      get(): Number[] {
        return this.watcherIds
      },
      set(newValue: Number[]) {
        this.$emit('update-watchers', newValue)
      },
    },
    avatarSize(): number {
      if (this.$screen.isDesktop(this.$mq)) {
        return 40
      }
      if (this.$screen.isTablet(this.$mq)) {
        return 24
      }
      if (this.$screen.isMobile(this.$mq)) {
        return 16
      }
    },
    isCommentActive(): boolean {
      return this.mode === 'comment'
    },
    isForConversation(): boolean {
      return this.type === EEntities.MAILBOXES
    },
    isReplyActive(): boolean {
      return this.mode === 'reply'
    },
    placeholder(): string {
      if (this.isReplyActive) {
        if (this.isInternalCustomerEmpty) {
          return ''
        } else if (this.internalCustomer.name !== this.internalCustomer.email) {
          return `Reply to ${this.internalCustomer.name} (${
            this.internalCustomer.email
          })...`
        } else {
          return `Reply to ${this.internalCustomer.email}`
        }
      }
      if (this.isForConversation) {
        return `Leave a comment. ${
          this.internalCustomer.name
        } won't see this...`
      }
      return `Leave a comment...`
    },

    editorClass(): string {
      if (this.expandEditor) {
        return 'comment-or-reply__body-message-editor--open'
      }

      return 'comment-or-reply__body-message-editor--default'
    },

    showStatusesSplayed(): boolean {
      return this.statusOptions.length > 1 && this.statusOptions.length <= 3
    },

    curStatus(): IStatusForSelection {
      /*
        Note: This can be "undefined" for a brief period of time on a meta-data update
        because the statusID prop from the task/convo hasn't updated yet, while the new status options have.
        This should be more rare now because the fetchTask/ConvoData() method is called first (previously it was
        last) in the Promisesof the task/convo detail fetchData() method. But, still it's a race condition,
        so it's not guaranteed. All told, this is why we do a v-if="curStatus" on the <status-selection-button> above. */
      return this.statusOptions.find(s => s.id === this.statusId)
    },

    splayedStatuses(): IStatusForSelection[] {
      if (!this.showStatusesSplayed) {
        return null
      }
      /* Grab the statuses after the first one (which is the current one) to display.*/
      return this.statusOptions.slice(1)
    },

    expandEditor(): Boolean {
      return this.isEditing && !this.isPostSubmitPreRefetchData
    },

    moveStatusDirection(): string {
      return this.$screen.isMobilePortrait(this.$mq) ? 'down' : 'right'
    },

    submitButtonText(): string {
      const statusChanged: boolean = this.statusId !== this.formData.statusID
      const priorityChanged: boolean =
        this.priorityId !== this.formData.priorityID
      const assigneeChanged: boolean =
        this.assigneeId !== this.formData.assigneeID
      const dueDateChanged: boolean = this.dueDate !== this.formData.dueDate

      const anyChanged: boolean =
        statusChanged || priorityChanged || assigneeChanged || dueDateChanged

      let result
      if (this.isCommentActive) {
        result = 'Add Comment'
      } else {
        if (this.isInternalCustomerEmpty) {
          result = 'Send Reply'
        } else {
          result = `Send Reply to ${this.internalCustomer.name}`
        }
      }

      if (!anyChanged) {
        return result
      }

      const shorten =
        (this.isReplyActive && this.$screen.isTabletOrMobile(this.$mq)) ||
        this.$screen.isMobilePortrait(this.$mq)

      if (shorten) {
        return (result += ' & Update')
      }

      result += ' & Update '

      const changeArray = new Array()

      if (statusChanged) {
        changeArray.push('Status')
      }
      if (priorityChanged) {
        changeArray.push('Priority')
      }
      if (assigneeChanged) {
        changeArray.push('Assignee')
      }
      if (dueDateChanged) {
        changeArray.push('Due Date')
      }

      for (let i = 0; i < changeArray.length; i++) {
        if (i > 0) {
          if (changeArray.length > 2 && i < changeArray.length - 1) {
            result += ', '
          } else {
            result += ' and '
          }
        }
        result += changeArray[i]
      }

      return result
    },
  },

  watch: {
    dueDate(newVal) {
      this.formData.dueDate = newVal
    },
    statusId(newVal) {
      this.formData.statusID = newVal
    },
    priorityId(newVal) {
      this.formData.priorityID = newVal
    },
    assigneeId(newVal) {
      this.formData.assigneeID = newVal
    },
    cc(newVal) {
      this.formData.cc = newVal
    },
    /* Scenario -- updated from customer name area on conversation-detail-header, override any
       update made to the "to" field here. */
    customer(newVal) {
      this.internalCustomer = newVal
      this.formData.to = newVal.email
    },
    'formData.message': {
      handler(value) {
        this.saveTextToLocalStorage(value)
      },
    },
    isEditing: {
      immediate: true,
      handler(value) {
        this.$emit('editor-opened', value)
      },
    },
    isSubmitting: {
      immediate: true,
      handler(newVal: Boolean, oldVal: Boolean) {
        // A finished submission...
        if (oldVal && !newVal) {
          this.isEditing = false
        }
      },
    },
    isPostSubmitPreRefetchData: {
      immediate: true,
      handler(newVal: Boolean, oldVal: Boolean) {
        // Post submission but refetching detail data...
        if (!oldVal && newVal) {
          this.clearEditor()
        }
      },
    },
  },

  mounted() {
    this.initializeFormData()
    this.initializeEditorSetup()
  },

  methods: {
    saveTextToLocalStorage(value: string) {
      if (this.isTextEmpty(value)) {
        LocalStore.clearCommentOrReplyText(this.itemId)
        return
      }

      LocalStore.setCommentOrReplyText(this.itemId, value)
    },
    getSavedTextFromLocalStorage() {
      const saved = LocalStore.getCommentOrReplyText(this.itemId)

      if (this.isTextEmpty(saved)) {
        return ''
      }

      return saved
    },

    /* Text editor setup */
    initializeEditorSetup() {
      // If previous mode was a reply, re-enable the reply...
      if (LocalStore.isDefaultToReplyMode(this.itemId)) {
        this.updateMode('reply')
      }
      // See if we have saved text from local storage...
      this.formData.message = this.getSavedTextFromLocalStorage()
      // If we have data, expose editing mode...
      if (!this.isTextEmpty(this.formData.message)) {
        this.isEditing = true
      }
    },

    isTextEmpty(text: string) {
      return text === '<p><br></p>' || text === null || text === ''
    },

    enableComment() {
      this.updateMode('comment')
      LocalStore.clearDefaultToReplyMode(this.itemId)
      this.openEditor()
    },

    enableReply() {
      this.updateMode('reply')
      LocalStore.setDefaultToReplyMode(this.itemId, true)
      this.openEditor()
    },

    updateMode(mode: string) {
      this.mode = mode
    },

    clearEditor() {
      // Clear local storage text settings...
      LocalStore.clearCommentOrReplyText(this.itemId)
      this.internalAttachments = []
      this.formData.message = ''
      this.formData.listAtMentionIDs = []
      this.formData.listFileUploadIDs = []
    },

    validateSubmitComment() {
      const noAttachments = this.formData.listFileUploadIDs.length === 0
      const textEmpty = this.isTextEmpty(this.formData.message)
      if (textEmpty && noAttachments) {
        this.$messaging.showError(`Please enter a comment.`)
        return
      }
      this.submitComment()
    },

    validateSubmitReply() {
      const noAttachments = this.formData.listFileUploadIDs.length === 0
      const textEmpty = this.isTextEmpty(this.formData.message)
      if (textEmpty && noAttachments) {
        this.$messaging.showError(`Please enter a reply.`)
        return
      }
      this.submitReply()
    },

    async submitComment() {
      const formData = omit(this.formData, ['to', 'cc'])
      this.$emit('submit', formData)
      await this.$nextTick()
    },

    async submitReply() {
      this.$emit('submit', this.formData)
      await this.$nextTick()
    },

    insertMentions(matches: any) {
      this.formData.listAtMentionIDs = matches
    },

    addFileHandler(files: IAttachment[]) {
      this.isEditing = true
      this.internalAttachments = files
      this.formData.listFileUploadIDs = files.map(item => item.id)
    },

    /**
     * Remove file from FormData
     */
    async removeFileHandler(id: number) {
      try {
        await FilesAPI.delete_Delete(id)
        this.internalAttachments = this.internalAttachments.filter(
          item => item.id !== id,
        )
        this.formData.listFileUploadIDs = this.internalAttachments.map(
          item => item.id,
        )
      } catch (error) {
        this.$messaging.showGenericError()
      }
    },

    initializeFormData() {
      this.formData.assigneeID = this.assigneeId
      this.formData.statusID = this.statusId
      this.formData.priorityID = this.priorityId
      this.formData.dueDate = this.dueDate
      this.formData.listWatcherIDs = this.watcherIds

      if (this.isForConversation) {
        this.formData.to = this.customer.email
        this.formData.cc = this.cc

        /* Decoupling ref from customer */
        this.internalCustomer = {
          email: this.customer.email,
          name: this.customer.name,
          id: this.customer.id,
        }
      }
    },

    updateToField(email: string) {
      this.formData.to = email
      this.internalCustomer = {
        email,
        name: email,
        id: 0,
      }
    },

    openEditor() {
      this.isEditing = true
      this.autoFocusEditor = true
    },
  },
})
